import { FBA_INVENTORY_FRESHNESS_MS } from "@adpilot/shared";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { displayedInventoryQuantity, type InventoryListing } from "./product-listing-helpers";

export function InventoryQuantity({
  listing,
  loading = false,
}: {
  listing: InventoryListing;
  loading?: boolean;
}) {
  const { t } = useTranslation("campaignCreation");
  const quantity = displayedInventoryQuantity(listing);
  // Keep the number's footprint while hiding it so refreshing never resizes the row or column.
  const value = (
    <div className="inline-grid min-w-5" data-inventory-quantity aria-busy={loading}>
      <span
        className={`col-start-1 row-start-1 ${loading ? "invisible" : ""}`}
        aria-hidden={loading || undefined}
      >
        {quantity ?? t("products.inventoryUnknown")}
      </span>
      {loading ? <Skeleton className="col-start-1 row-start-1 h-3.5 w-full self-center" /> : null}
    </div>
  );
  if (!listing.inventorySources || listing.inventorySources.length <= 1) {
    return value;
  }
  return (
    <div className="inline-flex items-center gap-1.5">
      {value}
      <Popover>
        <PopoverTrigger
          render={
            <button
              type="button"
              disabled={loading}
              className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-subtle hover:text-foreground"
              onClick={(event) => event.stopPropagation()}
            />
          }
          aria-label={t("products.inventorySources.view")}
        >
          <IconInfoCircle className="size-3.5" />
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="center"
          className="w-64 p-3"
          onClick={(event) => event.stopPropagation()}
        >
          <PopoverTitle>
            {t("products.inventorySources.title", { count: quantity ?? "—" })}
          </PopoverTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("products.inventorySources.description")}
          </p>
          <div className="mt-3 space-y-2">
            {listing.inventorySources.map((source) => {
              const syncedAt = new Date(source.syncedAt).getTime();
              const ageMs = Date.now() - syncedAt;
              const sourceIsFresh =
                Number.isFinite(syncedAt) && ageMs >= 0 && ageMs <= FBA_INVENTORY_FRESHNESS_MS;
              const sourceQuantity =
                source.sourceKind === "amazon_fulfilled"
                  ? listing.fbaFulfillableQuantity
                  : sourceIsFresh
                    ? source.quantity
                    : null;
              return (
                <div
                  key={source.channelCode}
                  className="flex items-center justify-between gap-3 text-xs"
                >
                  <span className="truncate text-muted-foreground">
                    {source.sourceName ?? t(`products.inventorySources.kind.${source.sourceKind}`)}
                    {source.sourceKind === "merchant_location"
                      ? ` · ${source.channelCode.slice(-4)}`
                      : ""}
                  </span>
                  <span className="font-medium text-foreground">{sourceQuantity ?? "—"}</span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
